import { LatLngLiteral } from "leaflet";

// Represents an accessibility support option provided by a clinic
// for V1Clinic.clinicAccessibilityOffereds, lookup table is V1AccessibilitySupport
export type ClinicAccessibilityOffered = {
  accessibilitySupportWriteIn?: string | null;
  accessibilitySupportText?: string | null; // Localized name of the accessibility support option.
  accessibilitySupportDescription?: string | null; // Localized text describing the accessibility support option.
  lineageText?: string | null; // Non-localized unique text identifying the accessibility support option.
};

// Represents a care setting option provided by a clinic
// for V1Clinic.clinicCareSettings, lookup table is V1CareSetting
export type ClinicCareSetting = {
  careSettingWriteIn?: string | null;
  careSettingText?: string | null; // Localized name of the care setting option.
  careSettingDescription?: string | null; // Localized text describing the care setting option.
};

// Represents a community served option provided by a clinic
// for V1Clinic.clinicCareSettings, lookup table is V1CommunityServed
export type ClinicCommunityServed = {
  patientCommunityWriteIn?: string | null;
  patientCommunityText?: string | null; // Localized name of the community served option.
  patientCommunityDescription?: string | null; // Localized text describing the community served option.
  lineageText?: string | null; // Non-localized unique text identifying the community served option.
};

// Represents a language used option provided by a clinic
// for V1Clinic.clinicLanguagesUseds, lookup table is V1Language
export type ClinicLanguageUsed = {
  languageWriteIn?: string | null;
  languageText?: string | null; // Localized name of the language used option.
  languageDescription?: string | null; // Localized text describing the language used option.
  lineageText?: string | null; // Non-localized unique text identifying the language used option.
};

// Represents a location of a clinic
// for V1Clinic.clinicLocations, no lookup table
export type ClinicLocation = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  gbpPlacesId?: string | null;
  cityText?: string | null;
  countyText?: string | null;
  stateText?: string | null;
  addrTypeText?: string | null; // Localized description of the address type.
  latLng?: LatLngLiteral | null | undefined;
};

// Represents a patient ethnicity option provided by a clinic
// for V1Clinic.clinicPatientEthnicities, no lookup table 
export type ClinicPatientEthnicity = {
  patientEthnicityWriteIn?: string | null;
  patientEthnicityText?: string | null; // Localized name of the community served option.
  patientEthnicityDescription?: string | null; // Localized text describing the community served option.
  lineageText?: string | null; // Non-localized unique text identifying the community served option.
};

// Represents a community served option provided by a clinic
// for V1Clinic.clinicPatientGenders, no lookup table 
export type clinicPatientGenders = {
  patientGenderText?: string | null; // Localized name of the community served option.
  patientGenderDescription?: string | null; // Localized text describing the community served option.
  lineageText?: string | null; // Non-localized unique text identifying the community served option.
};

// Represents a age group option provided by a clinic
// for V1Clinic.clinicPatientAgeGroups, no lookup table 
export type clinicPatientAgeGroups = {
  patientAgeGroupText?: string | null; // Localized name of the age group option.
  patientAgeGroupDescription?: string | null; // Localized text describing the age group option.
  lineageText?: string | null; // Non-localized unique text identifying the age group option.
};

// Represents a payment option provided by a clinic
// for V1Clinic.clinicPaymentOptions, lookup table is V1PaymentType
export type ClinicPaymentOption = {
  paymentTypeWriteIn?: string | null;
  paymentTypeText?: string | null; // Localized name of the payment option.
  paymentTypeDescription?: string | null; // Localized text describing the payment option.
  lineageText?: string | null; // Non-localized unique text identifying the payment option.
};

// Represents a phone number provided by a clinic
// for V1Clinic.clinicPhones, no lookup table 
export type ClinicPhone = {
  phone?: string | null; // Formatted phone number. Formatted in (###) ###-#### format.
  phoneTypeWriteIn?: string | null;
  phoneNumberTypeText?: string | null; // Localized description of the phone number type. Examples: "Front desk"
};

// Represents a Rae provided by a clinic
// for V1Clinic.clinicRaes, lookup table is V1Rae
export type ClinicRae = {
  raeAbbreviation?: string | null;
  raeDescription?: string | null;
  regionNumber?: number | null;
};

// Represents a service provided option provided by a clinic
// for V1Clinic.clinicServicesProvideds, lookup tables are V1ServiceProvided and V1ServiceCategory
export type ClinicServiceProvided = {
  serviceProvidedText?: string | null; // Localized name of the service provided option.
  serviceProvidedDescription?: string | null; // Localized text describing the service provided option.
  serviceCategoryText?: string | null; // Localized name of the service category option.

  // Analogous to the ServiceCategoryDescription property on the V1ServiceCategory class.
  serviceCategoryDescription?: string | null; // Localized text describing the service category option.
};

// Represents a self service provided option provided by a clinic
// for V1Clinic.clinicSSServicesProvideds, lookup tables are V1SSServiceProvided and V1ServiceCategory
export type ClinicSSServiceProvided = {
  ssserviceProvidedText?: string | null; // Localized name of the self service provided option.
  ssserviceProvidedDescription?: string | null; // Localized text describing the self service provided option.
  serviceCategoryText?: string | null; // Localized name of the service category option.

  // Analogous to the ServiceCategoryDescription property on the V1ServiceCategory class.
  serviceCategoryDescription?: string | null; // Localized text describing the service category option.
};

// Represents a telehealth option provided by a clinic
// for V1Clinic.clinicTelehealthOption, lookup table is V1TelehealthOption
export type ClinicTelehealthOption = {
  telehealthRestrictionWriteIn?: string | null;
  telehealthOptionText?: string | null; // Localized name of the telehealth option.
  telehealthOptionDescription?: string | null; // Localized text describing the telehealth option.
  lineageText?: string | null; // Non-localized unique text identifying the telehealth option.
};

// Represents a provider clinic
// for V1Clinic.providerClinics, no lookup table
export type ProviderClinic = {
  providerClinicId: number;
  providerId: number;
  providerType?: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
};

// Represents a Colorado healthcare provider
export type V1Clinic = {
  // The distance (in miles) from the provided zip code (if provided) to the clinic.
  // Note: this field is only included on the response when distanceSearchZipCode
  // and distanceSearchRadiusInMiles are provided.
  distanceInMiles?: number | null;
  description?: string | null;
  clinicId: number; // An integer unique ID of the clinic.
  accountId?: string | undefined; // A string unique ID of the clinic.
  parentAccountId?: string | null;
  entityId?: number | null;
  displayName?: string | null;
  primaryLicense?: string | null;
  originalDateOfLicensure?: string | null; // date
  npi?: number | null;
  csLicenseNumber?: string | null;
  sudLicenseNumber?: string | null;
  activeSudLicense?: boolean | null;
  rssoLicenseNumber?: string | null;
  activeRssoLicense?: boolean | null;
  mhDesignation?: string | null;
  activeMhDesignation?: boolean | null;
  fax?: string | null;
  tddTty?: string | null;
  open24Hours?: boolean | null;
  hoursOfOperationSunday?: string | null;
  hoursOfOperationMonday?: string | null;
  hoursOfOperationTuesday?: string | null;
  hoursOfOperationWednesday?: string | null;
  hoursOfOperationThursday?: string | null;
  hoursOfOperationFriday?: string | null;
  hoursOfOperationSaturday?: string | null;
  businessStatusId?: boolean | null;
  website?: string | null;
  circleProgram?: string | null;
  msoAffiliation?: string | null;
  alcoholDrugInvoluntaryCommitment?: string | null;
  appointmentRequired?: boolean | null;
  newPatientAcceptance?: boolean | null;
  publicTransit?: boolean | null;
  publicTransitWriteIn?: string | null;
  transportationAssistance?: boolean | null;
  transportationInfo?: string | null;
  archived?: boolean | null;
  archivalReason?: string | null;
  primaryTaxonomyId?: string | null;
  createdAt?: string | null; // date
  updatedAt?: string | null; // date
  updatedBy?: string | null;
  clinicAccessibilityOffereds?: ClinicAccessibilityOffered[] | null;
  clinicCareSettings?: ClinicCareSetting[] | null;
  clinicCommunityServeds?: ClinicCommunityServed[] | null;
  clinicLanguageUseds?: ClinicLanguageUsed[] | null;
  clinicLocations?: ClinicLocation[] | null;
  clinicPatientEthnicities?: ClinicPatientEthnicity[] | null;
  clinicPatientGenders?: clinicPatientGenders[] | null;
  clinicPatientAgeGroups?: clinicPatientAgeGroups[] | null;
  clinicPaymentOptions?: ClinicPaymentOption[] | null;
  clinicPhones?: ClinicPhone[] | null;
  clinicRaes?: ClinicRae[] | null;
  clinicServicesProvideds?: ClinicServiceProvided[] | null;
  clinicSSServicesProvideds?: ClinicSSServiceProvided[] | null;
  providerClinics?: ProviderClinic[] | null;
  clinicTelehealthOption?: ClinicTelehealthOption | null;
  latLng?: LatLngLiteral | null | undefined;
  searchRank: number | null | undefined;
  bhaProvider: boolean;
  isCmhc: boolean | null;
  bhasoNetwork: boolean | null;
  bhasoRegion: string | null;
};

// Represents a service category
// lookup table
// https://api.uat.pup.andromeda.joonix.net/v1/lookup-tables?includeServiceCategories=true
export type ServiceCategory = {
  id: string;
  text: string; // Localized name of the service category.
  description: string; // Localized text describing the service category.
};

export type V1ClinicPaginationResponse = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  Bhasos: string[];
  data: V1Clinic[]
};

export type SearchFilters = {
  page?: string;
  pageSize?: string;
  language?: string;
  displayNameFilter?: string;
  accessibilitySupportIds?: string[];
  careSettingIds?: string[];
  languageIds?: string[];
  patientAgeGroupIds?: string[];
  patientCommunityIds?: string[];
  paymentTypeIds?: string[];
  raeIds?: string[];
  serviceCategoryIds?: string[];
  serviceProvidedIds?: string[];
  telehealthOptions?: string[];
  openSunday?: string;
  openMonday?: string;
  openTuesday?: string;
  openWednesday?: string;
  openThursday?: string;
  openFriday?: string;
  openSaturday?: string;
  distanceSearchZipCode?: string;
  distanceSearchRadiusInMiles?: string;
  hours?: [];
};

export type LookupTablesApiParameters = {
  language: string | undefined | null;
  IncludeAccessibilitySupports?: boolean;
  IncludeCareSettings?: boolean;
  IncludeLanguages?: boolean;
  IncludePatientAgeGroups?: boolean;
  IncludePatientCommunities?: boolean;
  IncludePaymentTypes?: boolean;
  IncludeRaes?: boolean;
  IncludeServiceCategories?: boolean;
  IncludeServicesProvided?: boolean;
  IncludeTelehealthOptions?: boolean;
};

export function getFiltersFromSearchParams(
  searchParams: URLSearchParams
): SearchFilters {
  const filters: SearchFilters = {
    page: searchParams?.get("page") as string | undefined,
    pageSize: searchParams?.get("pageSize") as string | undefined,
    language: searchParams?.get("language") as string |  undefined,
    displayNameFilter: searchParams?.get("displayNameFilter") as string | undefined,
    accessibilitySupportIds: searchParams?.getAll("accessibilitySupportIds") as [] | undefined,
    careSettingIds: searchParams?.getAll("careSettingIds") as [] | undefined,
    languageIds: searchParams?.getAll("languageIds") as [] | undefined,
    patientAgeGroupIds: searchParams?.getAll("patientAgeGroupIds") as [] | undefined,
    patientCommunityIds: searchParams?.getAll("patientCommunityIds") as [] | undefined,
    paymentTypeIds: searchParams?.getAll("paymentTypeIds") as [] | undefined,
    raeIds: searchParams?.getAll("raeIds") as [] | undefined,
    serviceCategoryIds: searchParams?.getAll("serviceCategoryIds") as [] | undefined,
    serviceProvidedIds: searchParams?.getAll("serviceProvidedIds") as [] | undefined,
    telehealthOptions: searchParams?.getAll("telehealthOptions") as string[] | undefined,
    openSunday: searchParams?.get("openSunday") as  string | undefined,
    openMonday: searchParams?.get("openMonday") as  string | undefined,
    openTuesday: searchParams?.get("openTuesday") as  string | undefined,
    openWednesday: searchParams?.get("openWednesday") as  string | undefined,
    openThursday: searchParams?.get("openThursday") as  string | undefined,
    openFriday: searchParams?.get("openFriday") as  string | undefined,
    openSaturday: searchParams?.get("openSaturday") as  string | undefined,
    distanceSearchZipCode: searchParams?.get("distanceSearchZipCode") as string | undefined,
    distanceSearchRadiusInMiles: searchParams?.get("distanceSearchRadiusInMiles") as string | undefined,
  };

  if (!filters.accessibilitySupportIds) delete filters.accessibilitySupportIds;
  if (!filters.careSettingIds) delete filters.careSettingIds;
  if (!filters.languageIds) delete filters.languageIds;
  if (!filters.patientAgeGroupIds) delete filters.patientAgeGroupIds;
  if (!filters.patientCommunityIds) delete filters.patientCommunityIds;
  if (!filters.paymentTypeIds) delete filters.paymentTypeIds;
  if (!filters.raeIds) delete filters.raeIds;
  if (!filters.serviceCategoryIds) delete filters.serviceCategoryIds;
  if (!filters.serviceProvidedIds) delete filters.serviceProvidedIds;
  if (!filters.telehealthOptions) delete filters.telehealthOptions;
  if (!filters.openSunday) delete filters.openSunday;
  if (!filters.openMonday) delete filters.openMonday;
  if (!filters.openTuesday) delete filters.openTuesday;
  if (!filters.openWednesday) delete filters.openWednesday;
  if (!filters.openThursday) delete filters.openThursday;
  if (!filters.openFriday) delete filters.openFriday;
  if (!filters.openSaturday) delete filters.openSaturday;
  if (!filters.displayNameFilter) delete filters.displayNameFilter
  
  return filters;
}

export function countOptionalSearchFiltersUsed(
  filters: SearchFilters
): number {
  let count = 0;
  // pluck zip and miles out of filters
  const { distanceSearchZipCode, distanceSearchRadiusInMiles, ...optionalFilters } = filters;
  if (optionalFilters) {
    for (const [_filter_k, filter_v] of Object.entries(optionalFilters)) {
      if (filter_v?.length !== 0) {
        count += 1;
      }
    }
  }
  return count;
}

export const getAppliedOptionalFiltersCount = (filters: SearchFilters | undefined) => {
  //console.log(`filters are ${JSON.stringify(filters)}`);
  const countFilters = 
    // (!!filters?.page ? 1 : 0)
    // + (!!filters?.pageSize ? 1 : 0)
    // + (!!filters?.language ? 1 : 0)
    // + (!!filters?.displayNameFilter ? 1 : 0)
    (!!filters?.accessibilitySupportIds && filters.accessibilitySupportIds.length > 0 ? 1 : 0)
    // + (!!filters?.careSettingIds ? 1 : 0)
    // + (!!filters?.language && filters.language.length > 0 ? 1 : 0)
    + (!!filters?.languageIds && filters.languageIds.length > 0 ? 1 : 0)
    + (!!filters?.patientAgeGroupIds && filters.patientAgeGroupIds.length > 0 ? 1 : 0)
    + (!!filters?.patientCommunityIds && filters.patientCommunityIds.length > 0 ? 1 : 0)
    + (!!filters?.paymentTypeIds && filters.paymentTypeIds.length > 0 ? 1 : 0)
    // + (!!filters?.raeIds ? 1 : 0)
    + (!!filters?.serviceCategoryIds && filters.serviceCategoryIds.length > 0 ? 1 : 0)
    + (!!filters?.serviceProvidedIds && filters.serviceProvidedIds.length > 0? 1 : 0)
    + (!!filters?.telehealthOptions && filters.telehealthOptions.length > 0 ? 1 : 0) 
     + (!!filters?.openSunday && filters.openSunday.length > 0 ? 1 : 0)
     + (!!filters?.openMonday ? 1 : 0)
     + (!!filters?.openTuesday ? 1 : 0)
     + (!!filters?.openWednesday ? 1 : 0)
     + (!!filters?.openThursday ? 1 : 0)
     + (!!filters?.openFriday ? 1 : 0)
     + (!!filters?.openSaturday ? 1 : 0)
    // (!!filters?.distanceSearchZipCode ? 1 : 0)
    // (!!filters?.distanceSearchRadiusInMiles ? 1 : 0)
    ;
  // const countFilters = 0;
  return countFilters;
}

export const getFiltersWithOptionalCleared = (filters: SearchFilters | undefined) => {
  const updatedFilters = {
    ...filters,
    page:filters?.page || '',
    pageSize:filters?.pageSize || '',
    language: filters?.language || '',
    // displayNameFilter: '',    
    accessibilitySupportIds: [],
    // careSettingIds: [],
    languageIds: [],
    patientAgeGroupIds: [],
    patientCommunityIds: [],
    paymentTypeIds: [],
    // raeIds: [],
    serviceCategoryIds: [],
    serviceProvidedIds: [],
    telehealthOptions: [],
    // hours: []
  };
  
  delete updatedFilters.openSunday;
  delete updatedFilters.openMonday;
  delete updatedFilters.openTuesday;
  delete updatedFilters.openWednesday;
  delete updatedFilters.openThursday;
  delete updatedFilters.openFriday;
  delete updatedFilters.openSaturday;

  return updatedFilters;
};

export function clearHourFilters(filters:SearchFilters) {
  const updatedFilters = {
    ...filters,
    page:filters?.page || '',
    pageSize:filters?.pageSize || '',
    language: filters?.language || '',
    distanceSearchZipCode: filters?.distanceSearchZipCode || '',
    distanceSearchRadiusInMiles: filters?.distanceSearchRadiusInMiles || '',
    // displayNameFilter: '',    
    accessibilitySupportIds: filters?.accessibilitySupportIds || [],
    // careSettingIds: [],
    languageIds: filters?.languageIds || [],
    patientAgeGroupIds: filters?.patientAgeGroupIds || [],
    patientCommunityIds: filters?.patientCommunityIds || [],
    paymentTypeIds: filters?.paymentTypeIds || [],
    // raeIds: [],
    serviceCategoryIds: filters?.serviceCategoryIds || [],
    serviceProvidedIds: filters?.languageIds || [],
    telehealthOption: filters?.telehealthOptions || []
  }
 
  delete updatedFilters.openSunday;
  delete updatedFilters.openMonday;
  delete updatedFilters.openTuesday;
  delete updatedFilters.openWednesday;
  delete updatedFilters.openThursday;
  delete updatedFilters.openFriday;
  delete updatedFilters.openSaturday;

  return updatedFilters;  
}

export const EMPTY_SEARCH_FILTERS: SearchFilters = {
  page: '',
  pageSize: '',
  language: '',
  displayNameFilter: '',
  accessibilitySupportIds: [],
  careSettingIds: [],
  languageIds: [],
  patientAgeGroupIds: [],
  patientCommunityIds: [],
  paymentTypeIds: [],
  raeIds: [],
  serviceCategoryIds:  [],
  serviceProvidedIds:  [],
  telehealthOptions:  [],
  openSunday: '',
  openMonday:  '',
  openTuesday:  '',
  openWednesday:  '',
  openThursday:  '',
  openFriday:  '',
  openSaturday:  '',
  distanceSearchZipCode:  '',
  distanceSearchRadiusInMiles:  '',
  hours:[],
};

